import React, { useMemo, useState, Component } from 'react';
import { motion } from 'framer-motion';
import { Palette, Boxes, UserCog, Gauge, Square, Sparkles } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { AdminTabs } from '../shared/AdminTabs';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { Card } from '../../../components/ui/Card';
import { PRIMARY, SUCCESS, WARNING, DANGER, INFO, XP, NEUTRAL, ROLE_COLORS, MASTERY, MASTERY_THRESHOLDS, RADIUS, SHADOW, BADGE_VARIANTS, MODULE_COLOR_MAP } from '../../../config/designTokens';
// ─── Tab config ──────────────────────────────────────────────────────────────
type TabId = 'colors' | 'modules' | 'roles' | 'mastery' | 'shape';
const TABS: Array<{
  id: TabId;
  label: string;
  icon: React.ReactNode;
}> = [{
  id: 'colors',
  label: 'Kleuren',
  icon: <Palette className="w-4 h-4" data-id="element-6727" />
}, {
  id: 'modules',
  label: 'Modules',
  icon: <Boxes className="w-4 h-4" data-id="element-6728" />
}, {
  id: 'roles',
  label: 'Rollen',
  icon: <UserCog className="w-4 h-4" data-id="element-6729" />
}, {
  id: 'mastery',
  label: 'Mastery',
  icon: <Gauge className="w-4 h-4" data-id="element-6730" />
}, {
  id: 'shape',
  label: 'Vorm',
  icon: <Square className="w-4 h-4" data-id="element-6731" />
}];
const TAB_DESCRIPTION: Record<TabId, string> = {
  colors: 'Brand- en semantische status-tokens. Map op betekenis, niet op hue — danger is rood, warning amber, los van elkaar.',
  modules: 'Het canonieke kleur-palet per leer-module, inclusief de subtiele per-wereld practice-tint (APP_FEEL §3.1).',
  roles: 'Kleuridentiteit per account-rol — teacher violet, parent emerald, learner oranje, admin amber.',
  mastery: 'De vier beheersings-banden met eigen drempels (75/55/35). Hergebruikt nooit status-kleuren: beheersing ≠ correctheid.',
  shape: 'De getokeniseerde radius- en shadow-schaal plus de badge-varianten die de hele app vormgeven.'
};
// ─── Page ──────────────────────────────────────────────────────────────────
/**
 * DesignSystemPage — dedicated, read-only, LIVE showcase of the design-system
 * SSOT (src/config/designTokens.ts). Every swatch reads its value live from the
 * token export at render time, so the surface can never drift from the running
 * app. Tailwind class strings are rendered as-is (they all live in the token
 * source, so they survive purge); raw hex values use inline styles.
 */
export function DesignSystemPage() {
  const [activeTab, setActiveTab] = useState<TabId>('colors');
  const moduleCount = useMemo(() => Object.keys(MODULE_COLOR_MAP).length, []);
  return <PageLayout title="Design System" subtitle="De design-tokens SSOT — live uit de code gelezen, nooit gekopieerd" actions={<ConnectionBadge status="live" data-id="element-6733" />} breadcrumbs={[{
    label: 'Inhoud'
  }, {
    label: 'Design System'
  }]} data-id="element-6732">
      {/* Summary header */}
      <Card className="p-5 mb-5" data-id="element-6734">
        <div className="flex items-start gap-4" data-id="element-6735">
          <div className="w-10 h-10 rounded-xl bg-gray-900 text-white flex items-center justify-center flex-shrink-0" data-id="element-6736">
            <Palette className="w-5 h-5" data-id="element-6737" />
          </div>
          <div className="min-w-0" data-id="element-6738">
            <h2 className="text-lg font-bold text-gray-900 leading-tight" data-id="element-6739">
              Design-tokens
            </h2>
            <p className="text-sm text-gray-600 mt-1 leading-relaxed" data-id="element-6740">
              Eén canonieke bron voor kleur, vorm en module-palet:{' '}
              <code className="font-mono text-[12px] text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-6741">
                src/config/designTokens.ts
              </code>
              . Bevat o.a.{' '}
              <span className="font-semibold text-gray-900 tabular-nums" data-id="element-6742">
                {moduleCount}
              </span>{' '}
              module-kleursets. Componenten importeren hier vandaan in plaats
              van kleurklassen te hardcoden.
            </p>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <AdminTabs tabs={TABS} activeTab={activeTab} onTabChange={id => setActiveTab(id as TabId)} data-id="element-6743" />

      <p className="text-sm text-gray-500 mb-5" data-id="element-6744">{TAB_DESCRIPTION[activeTab]}</p>

      <motion.div key={activeTab} initial={{
      opacity: 0,
      y: 6
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.22
    }} role="tabpanel" id={`tabpanel-${activeTab}`} data-id="element-6745">
        {activeTab === 'colors' && <ColorsTab data-id="element-6746" />}
        {activeTab === 'modules' && <ModulesTab data-id="element-6747" />}
        {activeTab === 'roles' && <RolesTab data-id="element-6748" />}
        {activeTab === 'mastery' && <MasteryTab data-id="element-6749" />}
        {activeTab === 'shape' && <ShapeTab data-id="element-6750" />}
      </motion.div>
    </PageLayout>;
}
// ─── Colors tab ──────────────────────────────────────────────────────────────
function ColorsTab() {
  const tokens = [{
    name: 'Primary',
    token: PRIMARY,
    note: 'Brand / oranje'
  }, {
    name: 'Success',
    token: SUCCESS,
    note: 'Correct / beheerst'
  }, {
    name: 'Warning',
    token: WARNING,
    note: 'Let op (niet destructief)'
  }, {
    name: 'Danger',
    token: DANGER,
    note: 'Fout / destructief'
  }, {
    name: 'Info',
    token: INFO,
    note: 'Neutraal informatief'
  }, {
    name: 'XP',
    token: XP,
    note: 'Gamification'
  }, {
    name: 'Neutral',
    token: NEUTRAL,
    note: 'Ghost / oppervlak'
  }] as const;
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-6751">
      {tokens.map(({
      name,
      token,
      note
    }) => <Card key={name} className="p-4" data-id="element-6752">
          <div className={`h-16 w-full rounded-xl ${token.bg} border border-black/5`} aria-hidden="true" data-id="element-6753" />
          <div className="mt-3" data-id="element-6754">
            <div className="flex items-center justify-between gap-2" data-id="element-6755">
              <h4 className="text-sm font-semibold text-gray-900" data-id="element-6756">{name}</h4>
              <span className="text-[11px] text-gray-400" data-id="element-6757">{note}</span>
            </div>
            <dl className="mt-2 space-y-1 text-[11px]" data-id="element-6758">
              <SwatchRow label="Fill" cls={token.bg} data-id="element-6759" />
              <SwatchRow label="Tekst" cls={token.text} data-id="element-6760" />
              {'border' in token && <SwatchRow label="Border" cls={token.border} data-id="element-6761" />}
            </dl>
          </div>
        </Card>)}
    </div>;
}
function SwatchRow({
  label,
  cls
}: {
  label: string;
  cls: string;
}) {
  return <div className="flex items-center justify-between gap-2" data-id="element-6762">
      <dt className="text-gray-500" data-id="element-6763">{label}</dt>
      <dd data-id="element-6764">
        <code className="font-mono text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-6765">
          {cls}
        </code>
      </dd>
    </div>;
}
// ─── Modules tab ─────────────────────────────────────────────────────────────
function ModulesTab() {
  const entries = Object.entries(MODULE_COLOR_MAP);
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-6766">
      {entries.map(([key, set]) => <Card key={key} className="overflow-hidden p-0" data-id="element-6767">
          {/* Wereld-tint wash header met fill + hex specimen */}
          <div className="px-4 py-4 border-b border-black/5" style={{
        backgroundColor: set.tint
      }} data-id="element-6768">
            <div className="flex items-center gap-3" data-id="element-6769">
              <span className={`w-9 h-9 rounded-xl ${set.bg} shadow-sm border border-black/5 flex-shrink-0`} aria-hidden="true" data-id="element-6770" />
              <div className="min-w-0" data-id="element-6771">
                <code className="block font-mono text-xs text-gray-800 truncate" data-id="element-6772">
                  {key}
                </code>
                <span className="text-[11px] text-gray-500" data-id="element-6773">
                  tint {set.tint}
                </span>
              </div>
            </div>
          </div>
          <div className="p-4 space-y-1 text-[11px]" data-id="element-6774">
            <div className="flex items-center justify-between gap-2" data-id="element-6775">
              <span className="text-gray-500" data-id="element-6776">Hex (ring)</span>
              <span className="inline-flex items-center gap-1.5" data-id="element-6777">
                <span className="w-3 h-3 rounded-full border border-black/10" style={{
              backgroundColor: set.ring
            }} aria-hidden="true" data-id="element-6778" />
                <code className="font-mono text-gray-700" data-id="element-6779">{set.ring}</code>
              </span>
            </div>
            <SwatchRow label="Gradient" cls={`${set.from} ${set.to}`} data-id="element-6780" />
            <SwatchRow label="Tekst" cls={set.text} data-id="element-6781" />
            <SwatchRow label="Light" cls={set.light} data-id="element-6782" />
          </div>
        </Card>)}
    </div>;
}
// ─── Roles tab ───────────────────────────────────────────────────────────────
function RolesTab() {
  const ROLE_LABELS: Record<string, string> = {
    teacher: 'Leerkracht',
    parent: 'Ouder',
    learner: 'Leerling',
    admin: 'Beheerder'
  };
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-6783">
      {Object.entries(ROLE_COLORS).map(([role, set]) => <Card key={role} className="p-4" data-id="element-6784">
          <div className={`rounded-xl border px-4 py-3 ${set.bg} ${set.border}`} data-id="element-6785">
            <div className="flex items-center gap-3" data-id="element-6786">
              <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${set.iconBg} ${set.iconText}`} data-id="element-6787">
                <UserCog className="w-4 h-4" data-id="element-6788" />
              </span>
              <div data-id="element-6789">
                <p className={`text-sm font-semibold ${set.text}`} data-id="element-6790">
                  {ROLE_LABELS[role] ?? role}
                </p>
                <span className={`inline-block mt-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${set.badgeBg} ${set.badgeText}`} data-id="element-6791">
                  {role}
                </span>
              </div>
            </div>
          </div>
          <dl className="mt-3 space-y-1 text-[11px]" data-id="element-6792">
            <SwatchRow label="Achtergrond" cls={set.bg} data-id="element-6793" />
            <SwatchRow label="Tekst" cls={set.text} data-id="element-6794" />
            <SwatchRow label="Border" cls={set.border} data-id="element-6795" />
          </dl>
        </Card>)}
    </div>;
}
// ─── Mastery tab ─────────────────────────────────────────────────────────────
function MasteryTab() {
  const BAND_LABELS: Record<string, string> = {
    strong: 'Sterk beheerst',
    solid: 'Degelijk',
    developing: 'In ontwikkeling',
    attention: 'Aandacht nodig'
  };
  const thresholds = MASTERY_THRESHOLDS as Record<string, number>;
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-6796">
      {Object.entries(MASTERY).map(([band, set]) => <Card key={band} className="p-4" data-id="element-6797">
          <div className="flex items-center justify-between mb-3" data-id="element-6798">
            <span className="w-9 h-9 rounded-full border border-black/10" style={{
          backgroundColor: set.ring
        }} aria-hidden="true" data-id="element-6799" />
            <span className="text-sm font-bold text-gray-900 tabular-nums" data-id="element-6800">
              {band in thresholds ? `≥ ${thresholds[band]}` : '< 35'}
            </span>
          </div>
          <h4 className="text-sm font-semibold text-gray-900" data-id="element-6801">
            {BAND_LABELS[band] ?? band}
          </h4>
          <span className="text-[11px] text-gray-400 capitalize" data-id="element-6802">{band}</span>
          <dl className="mt-3 space-y-1 text-[11px]" data-id="element-6803">
            <SwatchRow label="Fill" cls={set.bg} data-id="element-6804" />
            <div className="flex items-center justify-between gap-2" data-id="element-6805">
              <dt className="text-gray-500" data-id="element-6806">Ring (hex)</dt>
              <dd data-id="element-6807">
                <code className="font-mono text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-6808">
                  {set.ring}
                </code>
              </dd>
            </div>
          </dl>
        </Card>)}
    </div>;
}
// ─── Shape tab ───────────────────────────────────────────────────────────────
function ShapeTab() {
  return <div className="space-y-5" data-id="element-6809">
      {/* Radius */}
      <Card className="p-5" data-id="element-6810">
        <div className="flex items-center gap-2 mb-4" data-id="element-6811">
          <Square className="w-4 h-4 text-gray-400" data-id="element-6812" />
          <h4 className="text-sm font-semibold text-gray-900" data-id="element-6813">Radius-schaal</h4>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-id="element-6814">
          {Object.entries(RADIUS).map(([name, cls]) => <div key={name} className="text-center" data-id="element-6815">
              <div className={`h-16 w-full bg-orange-100 border-2 border-orange-300 ${cls}`} aria-hidden="true" data-id="element-6816" />
              <p className="mt-2 text-xs font-semibold text-gray-700 capitalize" data-id="element-6817">
                {name}
              </p>
              <code className="text-[10px] font-mono text-gray-400" data-id="element-6818">{cls}</code>
            </div>)}
        </div>
      </Card>

      {/* Shadow */}
      <Card className="p-5" data-id="element-6819">
        <div className="flex items-center gap-2 mb-4" data-id="element-6820">
          <Sparkles className="w-4 h-4 text-gray-400" data-id="element-6821" />
          <h4 className="text-sm font-semibold text-gray-900" data-id="element-6822">Shadow-schaal</h4>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 bg-gray-50 rounded-xl p-5" data-id="element-6823">
          {Object.entries(SHADOW).map(([name, cls]) => <div key={name} className="text-center" data-id="element-6824">
              <div className={`h-16 w-full bg-white rounded-xl ${cls}`} aria-hidden="true" data-id="element-6825" />
              <p className="mt-2 text-xs font-semibold text-gray-700 capitalize" data-id="element-6826">
                {name}
              </p>
              <code className="text-[10px] font-mono text-gray-400 break-all" data-id="element-6827">
                {cls}
              </code>
            </div>)}
        </div>
      </Card>

      {/* Badge variants */}
      <Card className="p-5" data-id="element-6828">
        <div className="flex items-center gap-2 mb-4" data-id="element-6829">
          <Boxes className="w-4 h-4 text-gray-400" data-id="element-6830" />
          <h4 className="text-sm font-semibold text-gray-900" data-id="element-6831">
            Badge-varianten
          </h4>
        </div>
        <div className="flex flex-wrap gap-2.5" data-id="element-6832">
          {Object.entries(BADGE_VARIANTS).map(([name, cls]) => <span key={name} className={`inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full border ${cls}`} data-id="element-6833">
              {name}
            </span>)}
        </div>
      </Card>
    </div>;
}